/**
 * V1.5 Generic Confirm-Modal für Projekt-Aktionen (Phase 5.12 + 5.13).
 *
 * Bündelt 6 Aktionen mit gleichem Muster (Confirm + optional/required Reason):
 *   complete · reactivate · block · unblock · qs-flag · qs-resolve
 *
 * Aufrufer steuert nur `kind` + öffnet/schließt. Modal mappt intern auf
 * api-Methode, Toast-Text und Reason-Pflicht.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError, api } from '../../lib/api.js';
import { useToast } from '../toast.js';
import { BEFUNDE_QUERY_KEY } from './befunde/befund-basis.js';
import { Modal } from './modal.js';

export type ActionKind = 'complete' | 'reactivate' | 'block' | 'unblock' | 'qs-flag' | 'qs-resolve';

type Config = {
  keyBase: string;
  reason: 'required' | 'optional' | 'none';
  call: (projectId: string, reason: string | undefined) => Promise<unknown>;
  tone: 'primary' | 'success' | 'danger' | 'warn';
};

const CONFIGS: Record<ActionKind, Config> = {
  complete: {
    keyBase: 'complete',
    // Abschlussregel #93: Der Server verlangt eine Begruendung, sobald das Projekt nicht in
    // der Handover-Phase steht. Der Aufrufer kennt die Phase und reicht sie ueber
    // `completeReason` herein; ohne diesen Hinweis bleibt es beim strengeren `required`
    // (nie ein Serverfehler, hoechstens eine Begruendung zu viel).
    reason: 'required',
    call: (id, reason) => api.completeProject(id, reason),
    tone: 'success',
  },
  reactivate: {
    keyBase: 'reactivate',
    reason: 'optional',
    call: (id, reason) => api.reactivateProject(id, reason),
    tone: 'primary',
  },
  block: {
    keyBase: 'block',
    reason: 'required',
    call: (id, reason) => api.setProjectBlocked(id, reason ?? ''),
    tone: 'danger',
  },
  unblock: {
    keyBase: 'unblock',
    reason: 'optional',
    call: (id, reason) => api.unblockProject(id, reason),
    tone: 'success',
  },
  'qs-flag': {
    keyBase: 'qs_flag',
    reason: 'optional',
    call: (id, reason) => api.flagQsWithBl(id, reason),
    tone: 'primary',
  },
  'qs-resolve': {
    keyBase: 'qs_resolve',
    reason: 'optional',
    call: (id, reason) => api.resolveQsWithBl(id, reason),
    tone: 'success',
  },
};

const ACTION_FALLBACKS: Record<
  string,
  { title: string; body: string; placeholder: string; submit: string; toast: string }
> = {
  complete: {
    title: 'Projekt abschließen',
    body: 'Das Projekt wird auf Status „abgeschlossen" gesetzt. Es bleibt sichtbar, aber Bearbeitung wird gesperrt.',
    placeholder: 'z. B. „Übergabe an Service erfolgt"',
    submit: 'Abschließen',
    toast: 'Projekt abgeschlossen',
  },
  reactivate: {
    title: 'Projekt reaktivieren',
    body: 'Das Projekt wird wieder auf aktiv gesetzt. Bearbeitung wird freigegeben.',
    placeholder: 'Grund für Reaktivierung (optional)',
    submit: 'Reaktivieren',
    toast: 'Projekt reaktiviert',
  },
  block: {
    title: 'Block setzen',
    body: 'Markiert das Projekt als blockiert. Wird in Mission Control + Atlas sichtbar.',
    placeholder: 'Was blockiert? (Pflicht)',
    submit: 'Block setzen',
    toast: 'Block gesetzt',
  },
  unblock: {
    title: 'Block lösen',
    body: 'Markiert das Projekt wieder als nicht blockiert.',
    placeholder: 'Optionale Notiz für Audit',
    submit: 'Block lösen',
    toast: 'Block gelöst',
  },
  qs_flag: {
    title: 'QS mit BL flaggen',
    body: 'Setzt das Flag „QS-mit-BL offen". Landet im BL-Arbeitsvorrat.',
    placeholder: 'Optionale Notiz für Audit',
    submit: 'Flag setzen',
    toast: 'QS-mit-BL geflaggt',
  },
  qs_resolve: {
    title: 'QS-mit-BL lösen',
    body: 'Markiert die QS-mit-BL als erledigt.',
    placeholder: 'Optionale Notiz für Audit',
    submit: 'Lösen',
    toast: 'QS-mit-BL gelöst',
  },
};

export function ProjectActionModal({
  kind,
  projectId,
  onClose,
  completeReason,
}: {
  kind: ActionKind | null;
  projectId: string;
  onClose: () => void;
  /**
   * Begruendungspflicht **fuer den Abschluss** (#93). Nur der Aufrufer weiss, in welcher Phase
   * das Projekt steht (`phase_is_handover`) — und davon haengt ab, ob der Server eine
   * Begruendung verlangt. Bewusst auf `complete` beschraenkt: Ein allgemeiner Ueberschreiber
   * koennte sonst versehentlich die Pflicht bei „Block setzen" aufweichen.
   * Ohne Angabe bleibt es beim strengeren `required` — nie ein Serverfehler.
   */
  completeReason?: 'required' | 'optional';
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (kind) setReason('');
  }, [kind]);

  const baseCfg = kind ? CONFIGS[kind] : null;
  const cfg =
    baseCfg && kind === 'complete' && completeReason
      ? { ...baseCfg, reason: completeReason }
      : baseCfg;
  const fb = cfg ? ACTION_FALLBACKS[cfg.keyBase]! : null;
  const title = cfg && fb ? t(`v2.action.${cfg.keyBase}_title`, fb.title) : '';
  const body = cfg && fb ? t(`v2.action.${cfg.keyBase}_body`, fb.body) : '';
  const placeholder = cfg && fb ? t(`v2.action.${cfg.keyBase}_placeholder`, fb.placeholder) : '';
  const submitLabel = cfg && fb ? t(`v2.action.${cfg.keyBase}_submit`, fb.submit) : '';
  const toastTitle = cfg && fb ? t(`v2.action.${cfg.keyBase}_toast`, fb.toast) : '';

  const mutation = useMutation({
    mutationFn: async () => {
      if (!cfg) throw new Error('no action');
      const r = reason.trim();
      if (cfg.reason === 'required' && !r) {
        throw new Error(t('v2.modal.reason_required', 'Begründung *'));
      }
      return cfg.call(projectId, r || undefined);
    },
    onSuccess: () => {
      if (toastTitle) toast.success(toastTitle);
      void queryClient.invalidateQueries({ queryKey: ['v2-project', projectId] });
      void queryClient.invalidateQueries({ queryKey: ['v2-project-history', projectId] });
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
      // PV-052 L2: Der Server hat abgewartet neu geprüft — Befunde frisch laden.
      void queryClient.invalidateQueries({ queryKey: BEFUNDE_QUERY_KEY });
      onClose();
    },
    onError: (err) => {
      const msg =
        err instanceof ApiError ? err.message : err instanceof Error ? err.message : 'Unknown';
      toast.error(t('v2.modal.action_failed', 'Aktion fehlgeschlagen'), msg);
    },
  });

  if (!cfg || !kind) return null;

  const toneColor = `var(--${cfg.tone})`;
  const onToneColor = cfg.tone === 'primary' ? 'var(--on-primary)' : 'white';

  return (
    // Haelt die getippte Begruendung — Hintergrundklick und Escape duerfen sie nicht
    // verwerfen. Geschlossen wird ueber „Abbrechen".
    <Modal
      open
      onClose={onClose}
      labelledBy="project-action-title"
      panelClassName="w-full max-w-md overflow-hidden"
      closeOnBackdrop={false}
      closeOnEscape={false}
    >
      <header className="px-5 py-4 border-b" style={{ borderColor: 'var(--line-soft)' }}>
        <h2
          id="project-action-title"
          className="text-sm font-medium"
          style={{ color: 'var(--ink)' }}
        >
          {title}
        </h2>
      </header>
      <div className="px-5 py-4 space-y-3">
        <p className="text-sm" style={{ color: 'var(--ink-2)' }}>
          {body}
        </p>
        {cfg.reason !== 'none' && (
          <div>
            <label
              className="block text-[11px] uppercase tracking-[0.12em] mb-1.5"
              style={{ fontFamily: 'var(--f-mono)', color: 'var(--ink-3)' }}
            >
              {cfg.reason === 'required'
                ? t('v2.modal.reason_required', 'Begründung *')
                : t('v2.modal.reason_optional', 'Begründung (optional)')}
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={placeholder}
              rows={3}
              className="w-full text-sm px-2 py-1.5 rounded outline-none resize-y"
              style={{
                background: 'var(--surface-2)',
                border: '1px solid var(--line-soft)',
                color: 'var(--ink)',
              }}
            />
            {kind === 'complete' && cfg.reason === 'required' && (
              // Sagt die Regel, bevor der Server sie mit 422 durchsetzt (#93).
              <p className="text-[11px] mt-1.5" style={{ color: 'var(--ink-3)' }}>
                {t(
                  'v2.action.complete_reason_hint',
                  'Außerhalb der Phase Handover ist eine Begründung Pflicht.',
                )}
              </p>
            )}
          </div>
        )}
      </div>
      <footer
        className="px-5 py-3 border-t flex items-center justify-end gap-2"
        style={{ borderColor: 'var(--line-soft)', background: 'var(--surface-2)' }}
      >
        <button
          type="button"
          onClick={onClose}
          disabled={mutation.isPending}
          className="text-xs px-3 py-1.5 rounded-md"
          style={{
            background: 'var(--surface)',
            color: 'var(--ink-2)',
            border: '1px solid var(--line-soft)',
          }}
        >
          {t('v2.modal.cancel', 'Abbrechen')}
        </button>
        <button
          type="button"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending || (cfg.reason === 'required' && !reason.trim())}
          className="text-xs px-3 py-1.5 rounded-md transition-opacity"
          style={{
            background: toneColor,
            color: onToneColor,
            border: 'none',
            opacity: mutation.isPending || (cfg.reason === 'required' && !reason.trim()) ? 0.5 : 1,
          }}
        >
          {mutation.isPending ? t('v2.modal.action_sending', 'Sendet…') : submitLabel}
        </button>
      </footer>
    </Modal>
  );
}
